import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import CreatableSelect from "react-select/creatable";
import EditIcon from "../../CustomCommons/EditIcon";
import ViewIcon from "../../CustomCommons/ViewIcon";
import Loader from "../../CustomCommons/Loader";
import { BASE_URL } from "../../Const/Url";
import { showToast } from "../../../utils/ToastHelper";
import { indexingStatus as indexingStatusOptions } from "../../CustomCommons/status";
import Editor from "../../Promotion/Blogs/Editor";

const DEFAULT_EDIT_FORM = {
  seo_type: "",
  primary_keyword: "",
  meta_title: "",
  meta_tags: [],
  meta_description: "",
  canonical_url: "",
  indexing: "index",
  set_schema: "",
  social_title: "",
  social_description: "",
  content: "",
};

const ENDPOINT = `${BASE_URL}api/v1/utility/admin/page-seo/`;

const SEOTable = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [seoRows, setSeoRows] = useState([]);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSeoData, setSelectedSeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editFormData, setEditFormData] = useState(DEFAULT_EDIT_FORM);

  const fetchSeoList = () => {
    setIsLoading(true);
    axios
      .get(ENDPOINT)
      .then((response) => {
        setSeoRows(response?.data?.data?.results || []);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || "Failed to load SEO page list.";
        showToast("error", message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchSeoList();
  }, []);

  const getFormattedDateTime = (dateValue) => {
    if (!dateValue) return "N/A";
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return "N/A";
    return parsedDate.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const buildEditFormData = (seoData) => {
    const normalizedTags = Array.isArray(seoData?.meta_tags)
      ? seoData.meta_tags
      : [];
    return {
      seo_type: seoData?.seo_type ?? "",
      primary_keyword: seoData?.primary_keyword || "",
      meta_title: seoData?.meta_title || "",
      meta_tags: normalizedTags.map((tag) => ({
        label: typeof tag === "string" ? tag : tag?.name || "",
        value: typeof tag === "string" ? tag : tag?.name || "",
      })),
      meta_description: seoData?.meta_description || "",
      canonical_url: seoData?.canonical_url || "",
      indexing: seoData?.indexing || "index",
      // Keep schema editable as string for safe round-trip in a textarea.
      set_schema:
        typeof seoData?.set_schema === "string"
          ? seoData.set_schema
          : seoData?.set_schema
          ? JSON.stringify(seoData.set_schema, null, 2)
          : "",
      social_title: seoData?.social_title || "",
      social_description: seoData?.social_description || "",
      content: seoData?.content || "",
    };
  };

  const handleOpenViewModal = (seoData) => {
    setSelectedSeoData(seoData);
    setViewModalOpen(true);
  };

  const handleOpenEditModal = (seoData) => {
    setSelectedSeoData(seoData);
    setEditFormData(buildEditFormData(seoData));
    setEditModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setSelectedSeoData(null);
    setViewModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setSelectedSeoData(null);
    setEditFormData(DEFAULT_EDIT_FORM);
    setEditModalOpen(false);
  };

  const handleEditInputChange = (event) => {
    const { name, value } = event.target;
    setEditFormData((previousData) => ({
      ...previousData,
      [name]: value,
    }));
  };

  const handleContentChange = (value) => {
    setEditFormData((previousData) => ({
      ...previousData,
      content: value,
    }));
  };

  const handleEditMetaTagsChange = (newTags) => {
    setEditFormData((previousData) => ({
      ...previousData,
      meta_tags: newTags || [],
    }));
  };

  const editPayload = useMemo(() => {
    const schemaText = editFormData?.set_schema?.trim();
    let parsedSchema = undefined;

    if (schemaText) {
      try {
        parsedSchema = JSON.parse(schemaText);
      } catch (error) {
        parsedSchema = schemaText;
      }
    }

    return {
      seo_type: editFormData.seo_type,
      primary_keyword: editFormData.primary_keyword?.trim() || "",
      meta_title: editFormData.meta_title?.trim() || "",
      meta_tags: (editFormData.meta_tags || [])
        .map((tag) => tag?.label?.trim())
        .filter(Boolean),
      meta_description: editFormData.meta_description?.trim() || "",
      canonical_url: editFormData.canonical_url?.trim() || "",
      indexing: editFormData.indexing || "index",
      set_schema: parsedSchema,
      social_title: editFormData.social_title?.trim() || "",
      social_description: editFormData.social_description?.trim() || "",
      content: editFormData.content?.trim() || "",
    };
  }, [editFormData]);

  const handleUpdateSeo = () => {
    if (!editFormData?.seo_type && editFormData?.seo_type !== 0) {
      showToast("error", "Invalid SEO type.");
      return;
    }

    setIsSubmitting(true);
    const updateUrl = `${ENDPOINT}/${editFormData.seo_type}/`;
    axios
      .patch(updateUrl, editPayload)
      .then(() => {
        showToast("success", "SEO data updated successfully.");
        handleCloseEditModal();
        fetchSeoList();
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.errors?.non_field_errors?.[0] ||
          "Failed to update SEO data.";
        showToast("error", errorMessage);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="categorey-parent">
      <div className="cotagorey-head mb-4">
        <h3>SEO List</h3>
      </div>

      <div style={{ display: "block", overflowX: "auto" }}>
        <table className="table_my my-3">
          <thead
            className="bg-dark text-white head-row mt-3"
            style={{ height: "50px" }}
          >
            <tr>
              <th style={{ fontSize: "16px", fontWeight: "normal", paddingLeft: "20px" }}>
                ID
              </th>
              <th style={{ fontSize: "16px", fontWeight: "normal" }}>
                Page Name
              </th>
              <th style={{ fontSize: "16px", fontWeight: "normal" }}>
                Last Updated
              </th>
              <th
                style={{
                  fontSize: "16px",
                  fontWeight: "normal",
                  width: "130px",
                  textAlign: "center",
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody className="tb">
            {seoRows?.length > 0 ? (
              seoRows.map((seoData) => (
                <tr key={seoData?.seo_type} className="table-row">
                  <td style={{ paddingLeft: "20px" }}>{seoData?.seo_type ?? "N/A"}</td>
                  <td>{seoData?.seo_type_display || "N/A"}</td>
                  <td>{getFormattedDateTime(seoData?.updated_at)}</td>
                  <td className="action">
                    <div className="d-flex gap-3 justify-content-center">
                      <div
                        className="edit-del blue"
                        onClick={() => handleOpenViewModal(seoData)}
                        title="View"
                      >
                        <ViewIcon />
                      </div>
                      <div
                        className="edit-del green"
                        onClick={() => handleOpenEditModal(seoData)}
                        title="Edit"
                      >
                        <EditIcon />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4">
                  <div className="not_found d-flex justify-content-center align-items-center py-4">
                    <h4 className="my-4">No Data Found</h4>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      <Modal show={viewModalOpen} onHide={handleCloseViewModal} size="xl" centered>
        <Modal.Header>
          <Modal.Title>SEO Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-12 mb-2">
              <strong>SEO Type: </strong>
              <span>{selectedSeoData?.seo_type ?? "N/A"}</span>
            </div>
            <div className="col-12 mb-2">
              <strong>Page Name: </strong>
              <span>{selectedSeoData?.seo_type_display || "N/A"}</span>
            </div>
            <div className="col-12 mb-2">
              <strong>Primary Keyword: </strong>
              <span>{selectedSeoData?.primary_keyword || "N/A"}</span>
            </div>
            <div className="col-12 mb-2">
              <strong>Meta Title: </strong>
              <span>{selectedSeoData?.meta_title || "N/A"}</span>
            </div>
            <div className="col-12 mb-2">
              <strong>Meta Tags: </strong>
              <span>
                {Array.isArray(selectedSeoData?.meta_tags) &&
                selectedSeoData?.meta_tags?.length > 0
                  ? selectedSeoData.meta_tags
                      .map((item) => (typeof item === "string" ? item : item?.name))
                      .filter(Boolean)
                      .join(", ")
                  : "N/A"}
              </span>
            </div>
            <div className="col-12 mb-2">
              <strong>Meta Description: </strong>
              <span>{selectedSeoData?.meta_description || "N/A"}</span>
            </div>
            <div className="col-12 mb-2">
              <strong>Canonical URL: </strong>
              <span>{selectedSeoData?.canonical_url || "N/A"}</span>
            </div>
            <div className="col-12 mb-2">
              <strong>Indexing: </strong>
              <span>{selectedSeoData?.indexing || "N/A"}</span>
            </div>
            <div className="col-12 mb-2">
              <strong>Schema: </strong>
              <span>
                {selectedSeoData?.set_schema
                  ? typeof selectedSeoData.set_schema === "string"
                    ? selectedSeoData.set_schema
                    : JSON.stringify(selectedSeoData.set_schema)
                  : "N/A"}
              </span>
            </div>
            <div className="col-12 mb-2">
              <strong>Social Title: </strong>
              <span>{selectedSeoData?.social_title || "N/A"}</span>
            </div>
            <div className="col-12 mb-2">
              <strong>Social Description: </strong>
              <span>{selectedSeoData?.social_description || "N/A"}</span>
            </div>
            <div className="col-12 mb-2">
              <strong>Content: </strong>
              <span>{selectedSeoData?.content || "N/A"}</span>
            </div>
            <div className="col-12 mb-2">
              <strong>Created At: </strong>
              <span>{getFormattedDateTime(selectedSeoData?.created_at)}</span>
            </div>
            <div className="col-12 mb-2">
              <strong>Updated At: </strong>
              <span>{getFormattedDateTime(selectedSeoData?.updated_at)}</span>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseViewModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={editModalOpen} onHide={handleCloseEditModal} size="xl" centered>
        <Modal.Header>
          <Modal.Title>Update SEO</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-12 col-md-6 mb-3">
              <p>SEO Type</p>
              <InputGroup>
                <Form.Control value={editFormData?.seo_type} disabled />
              </InputGroup>
            </div>
            <div className="col-12 col-md-6 mb-3">
              <p>Indexing Status</p>
              <Form.Select
                name="indexing"
                value={editFormData?.indexing}
                onChange={handleEditInputChange}
                style={{ backgroundColor: "#FAFAFA" }}
              >
                {indexingStatusOptions?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="col-12 col-md-6 mb-3">
              <p>Primary Keyword</p>
              <InputGroup>
                <Form.Control
                  name="primary_keyword"
                  value={editFormData?.primary_keyword}
                  onChange={handleEditInputChange}
                  style={{ backgroundColor: "#FAFAFA" }}
                />
              </InputGroup>
            </div>
            <div className="col-12 col-md-6 mb-3">
              <p>Meta Title</p>
              <InputGroup>
                <Form.Control
                  name="meta_title"
                  value={editFormData?.meta_title}
                  onChange={handleEditInputChange}
                  style={{ backgroundColor: "#FAFAFA" }}
                />
              </InputGroup>
            </div>

            <div className="col-12 mb-3">
              <p>Meta Tags</p>
              <CreatableSelect
                isMulti
                placeholder="Enter meta tags"
                onChange={handleEditMetaTagsChange}
                value={editFormData?.meta_tags}
              />
            </div>

            <div className="col-12 mb-3">
              <p>Meta Description</p>
              <InputGroup>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="meta_description"
                  value={editFormData?.meta_description}
                  onChange={handleEditInputChange}
                  style={{ backgroundColor: "#FAFAFA", resize: "none" }}
                />
              </InputGroup>
            </div>

            <div className="col-12 mb-3">
              <p>Canonical URL</p>
              <InputGroup>
                <Form.Control
                  name="canonical_url"
                  value={editFormData?.canonical_url}
                  onChange={handleEditInputChange}
                  style={{ backgroundColor: "#FAFAFA" }}
                />
              </InputGroup>
            </div>

            <div className="col-12 mb-3">
              <p>Schema</p>
              <InputGroup>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="set_schema"
                  value={editFormData?.set_schema}
                  onChange={handleEditInputChange}
                  style={{ backgroundColor: "#FAFAFA", resize: "none" }}
                  placeholder="Enter JSON or text schema"
                />
              </InputGroup>
            </div>

            <div className="col-12 col-md-6 mb-3">
              <p>Social Title</p>
              <InputGroup>
                <Form.Control
                  name="social_title"
                  value={editFormData?.social_title}
                  onChange={handleEditInputChange}
                  style={{ backgroundColor: "#FAFAFA" }}
                />
              </InputGroup>
            </div>

            <div className="col-12 col-md-6 mb-3">
              <p>Social Description</p>
              <InputGroup>
                <Form.Control
                  name="social_description"
                  value={editFormData?.social_description}
                  onChange={handleEditInputChange}
                  style={{ backgroundColor: "#FAFAFA" }}
                />
              </InputGroup>
            </div>

            <div className="col-12 mb-3">
              <p>Content</p>
              <div className="mb-3">
                <Editor
                  content={editFormData?.content || ""}
                  setContent={handleContentChange}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="dark" onClick={handleUpdateSeo} disabled={isSubmitting}>
            {isSubmitting ? "Updating..." : "Update"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SEOTable;